import React from "react";
import { ShieldAlert } from "lucide-react";
import { colorOf, fmtDateTime, schApi } from "@/lib/schApi";
import { Empty, ErrorBox, Loading, SummaryChip, useLoad } from "@/components/nds/windows/finance/parts";

/** Every active event carrying an overridden hard block or a confirmed staff warning. */
export default function SchConflictsContent() {
  const [state, load] = useLoad(() => schApi.get("/events", { conflicts_only: true, status: "active,completed" }), []);
  if (state.loading && !state.data) return <Loading testid="sch-conflicts-loading" />;
  if (state.error) return <ErrorBox testid="sch-conflicts-error" error={state.error} onRetry={load} />;
  const rows = state.data.events;
  const blocks = rows.reduce((n, e) => n + e.conflicts.filter((c) => c.severity === "hard_block").length, 0);
  return (
    <div data-testid="sch-conflicts" className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-3">
        <ShieldAlert className="h-4 w-4 text-nex-gold" />
        <SummaryChip tone="red" testid="sch-conflicts-summary">{blocks} overridden blocks</SummaryChip>
        <span className="text-xs text-nex-muted">{rows.length} events with exceptions</span>
      </div>
      <ul className="flex-1 overflow-y-auto divide-y divide-white/5" data-testid="sch-conflicts-list">
        {rows.map((e) => (
          <li key={e.id} data-testid={`sch-conflict-row-${e.id}`} className="px-5 py-3 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorOf(e) }} />
              <span className="text-nex-silver font-medium">{e.title}</span>
              <span className="text-xs text-nex-muted">{fmtDateTime(e.start_time)} · {e.location}</span>
            </div>
            {e.conflicts.map((c, i) => (
              <div key={i} className={`ml-4 text-xs rounded-sm px-2 py-1 ${c.severity === "hard_block" ? "bg-red-500/10 text-red-200" : "bg-amber-500/10 text-amber-200"}`}>
                <span className="font-mono text-[9px] uppercase mr-2">{c.severity === "hard_block" ? "Override" : "Warning"}</span>
                {c.message}<span className="opacity-70"> · {c.by?.name} · {fmtDateTime(c.at)}{c.reason ? ` · "${c.reason}"` : ""}</span>
              </div>
            ))}
          </li>
        ))}
        {!rows.length && <li><Empty testid="sch-conflicts-empty">No overridden conflicts. Every booking followed the rules.</Empty></li>}
      </ul>
    </div>
  );
}
